import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Panel,
} from '@bcecloud/portal-ui';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import { type MemoryFile, type MemoryVersionEntry } from '@/pages/assistant/shared';

/**
 * The memory edit form, at its own URL. Saves carry the loaded version for
 * the server's conflict check (409 = the assistant wrote in the meantime).
 */
export function MemoryEditPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const memoryId = useParams<{ memoryId: string }>().memoryId ?? '';
  const restore = (useLocation().state as { restore?: MemoryVersionEntry } | null)?.restore;
  const [form, setForm] = useState<{ description: string; content: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const memory = useQuery({
    queryKey: ['assistant-memory', memoryId],
    queryFn: ({ signal }) => getJson<MemoryFile>('/assistant/memories/file', { id: memoryId }, signal),
  });

  // Seed the form once the file loads — from the restored version when the
  // view page sent one, else from the current content.
  useEffect(() => {
    if (memory.data === undefined || form !== null) return;
    setForm({
      description: memory.data.description,
      content: restore?.content ?? memory.data.content,
    });
  }, [memory.data, form, restore]);

  const save = useMutation({
    mutationFn: (state: { description: string; content: string }) =>
      sendJson('put', '/assistant/memories/file', {
        path: memory.data?.path,
        content: state.content,
        description: state.description,
        expectedVersion: memory.data?.version,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assistant-memories'] });
      await queryClient.invalidateQueries({ queryKey: ['assistant-memory', memoryId] });
      navigate(`/assistant/memory/${memoryId}`);
    },
    onError: (error) => setNotice(errorMessage(error)),
  });

  if (memory.isPending) return <LoadingState label="Loading memory…" />;
  if (memory.isError) {
    return <ErrorNotice title="Memory not found" message={errorMessage(memory.error)} />;
  }
  if (form === null) return <LoadingState label="Loading memory…" />;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="AI ASSISTANT"
        title={memory.data.path}
        subtitle={
          restore
            ? `Restoring v${restore.version} — saving writes a new version`
            : 'Editing this memory — the assistant uses the saved version immediately'
        }
      />
      <Panel className="p-5">
        {notice === null ? null : (
          <div className="mb-3">
            <ErrorNotice inline title="Could not save" message={notice} />
          </div>
        )}
        <div className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-portal-micro font-medium text-portal-muted">
              What this memory holds
            </span>
            <input
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
              maxLength={200}
              className="w-full rounded-md border border-admin-line px-2 py-1.5 text-portal-small focus:border-brand/60 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-portal-micro font-medium text-portal-muted">
              Content
            </span>
            <textarea
              value={form.content}
              onChange={(event) => setForm({ ...form, content: event.target.value })}
              maxLength={8000}
              className="field-sizing-content min-h-64 w-full resize-y rounded-md border border-admin-line p-2 font-mono text-portal-micro leading-relaxed focus:border-brand/60 focus:outline-none"
            />
          </label>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={() => navigate(`/assistant/memory/${memoryId}`)}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || form.content.trim().length === 0}
            onClick={() => save.mutate(form)}
          >
            {save.isPending ? 'Saving…' : 'Save memory'}
          </Button>
        </div>
      </Panel>
    </section>
  );
}
